import { useCallback, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CopyIcon, PrinterIcon, ShoppingBagIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchVoucher } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnSecondary } from '../ui/styles';
import { money } from '../../core/format/currency';
import { received } from '../inbox/inboxRules';
import { salon } from '../../data/content';
import { ledgerKindLabel, signedAmount, sourceLabel, untilLabel, validForLabel, voucherStatusBadge } from './voucherLabels';
import { printVoucher } from './voucherDocument';
import { VoucherActions } from './VoucherActions';
import type { RequestOptions } from '../../core/types/backend';
import type { Voucher } from '../../core/types/vouchers';

/**
 * Eén bon: saldo, waarvoor hij geldt, tot wanneer, en het volledige grootboek. Opnieuw printen kan
 * altijd. Een fout herstel je met een correctieregel; een bon die kwijt is, blokkeer je.
 */
export function VoucherDetail() {
  const {
    id = ''
  } = useParams();
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
    } | null;
  };
  const back = {
    to: `/be/cadeaubonnen${state?.from ?? ''}`,
    label: 'Alle cadeaubonnen'
  };
  const load = useCallback((opts: RequestOptions) => fetchVoucher(id, opts), [id]);
  const {
    data: voucher,
    setData,
    status,
    reload
  } = useLatestLoad(load);
  if (status === 'loading' && !voucher) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-5364" data-mp="xcyn6ppqimgi">
        <Spinner data-id="element-5365" data-mp="1l3cicp3m91fb" /> Cadeaubon laden…
      </div>;
  if (status === 'error' && !voucher) return <LoadFailed title="Deze cadeaubon kon niet laden" onRetry={reload} data-id="element-5366" data-mp="1pxgpb8cvt9ge" />;
  if (!voucher) return <MissingItem title="Cadeaubon niet gevonden" back={back} data-id="element-5367" data-mp="1zp9uu4ic6p71" />;
  return <VoucherView voucher={voucher} back={back} onChanged={setData} data-id="element-5368" data-mp="1utaxhrf15wuh" />;
}
type ViewProps = {
  voucher: Voucher;
  back: {
    to: string;
    label: string;
  };
  onChanged: (next: Voucher) => void;
};
function VoucherView({
  voucher: v,
  back,
  onChanged
}: ViewProps) {
  const {
    marketing
  } = useAdmin();
  const design = marketing.designs.find(d => d.template === 'giftcard');
  const [printing, setPrinting] = useState(false);
  const badge = voucherStatusBadge[v.status];
  // Nieuwste bovenaan: de laatste beweging is wat ze meestal zoekt.
  const ledger = [...v.ledger].sort((a, b) => b.at.localeCompare(a.at));
  const print = async () => {
    setPrinting(true);
    const ok = await printVoucher(v, design, salon.name);
    setPrinting(false);
    if (!ok) toast.error('Printen lukte niet', {
      description: 'Sta pop-ups toe voor deze site en probeer het opnieuw.'
    });
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(v.code);
      toast.success('Code gekopieerd');
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: 'Selecteer de code en kopieer hem zelf.'
      });
    }
  };
  return <div data-id="element-5369" data-mp="3drqs4zvczo51">
      <PageHeader title={`Cadeaubon ···${v.codeLast4}`} description={`${sourceLabel[v.source]}${v.buyerName ? ` · ${v.buyerName}` : ''} · ${received(v.createdAt)}`} back={back} actions={<button type="button" onClick={() => void print()} disabled={printing} aria-busy={printing} className={btnSecondary} data-id="element-5371" data-mp="2q1cyj4x4lh8c">
            {printing ? <Spinner data-id="element-5372" data-mp="2uzeq6e8maovf" /> : <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5373" data-mp="2uyv9f0upe6iz" />}
            Printen of PDF
          </button>} data-id="element-5370" data-mp="3dout187rytf0" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]" data-id="element-5374" data-mp="22bsgqpcpkq0n">
        <div className="space-y-6" data-id="element-5375" data-mp="229oc256zjmzb">
          {/* Wat er nog op staat wint: groot, met de status ernaast. */}
          <section aria-labelledby="voucher-balance" className="rounded-lg border border-line bg-white p-4 sm:p-6" data-id="element-5376" data-mp="278irqy9novf7">
            <div className="flex items-start justify-between gap-4" data-id="element-5377" data-mp="8tbkvd3ca40u">
              <h2 id="voucher-balance" className="text-[13px] text-muted" data-id="element-5378" data-mp="8qyucbjimlc6">
                Saldo
              </h2>
              <Badge tone={badge.tone} data-id="element-5379" data-mp="mmawv53wxg3n">{badge.label}</Badge>
            </div>
            <p className="mt-1 font-display text-[2.4rem] font-light leading-tight tabular-nums text-ink" data-id="element-5380" data-mp="hsf5ili5mrhg">{money(v.balanceCents)}</p>
            <p className="text-[13px] text-muted" data-id="element-5381" data-mp="rjtav9wselii">van {money(v.initialCents)} bij uitgifte</p>
            <div className="mt-5 border-t border-line pt-4" data-id="element-5382" data-mp="15jc46wkxvsar">
              <VoucherActions voucher={v} onChanged={onChanged} data-id="element-5383" data-mp="15isq5qyeto20" />
            </div>
          </section>

          <Panel title="Geschiedenis" description="Alles wat er met deze bon gebeurde. Regels worden nooit gewijzigd of verwijderd." flush data-id="element-5384" data-mp="10l0vip6drnu4">
            <ol className="divide-y divide-line" data-id="element-5385" data-mp="32bui3wesd5bm">
              {ledger.map(e => <li key={e.id} className="flex items-start gap-4 px-4 py-3.5 sm:px-5" data-id="element-5386" data-mp="1htjwzy9qd57a">
                  <span className="min-w-0 flex-1" data-id="element-5387" data-mp="1hvwe697nzbja">
                    <span className="block text-[14px] text-ink" data-id="element-5388" data-mp="1mrlh8miw7akc">{ledgerKindLabel[e.kind]}</span>
                    <span className="block text-[12px] text-muted" data-id="element-5389" data-mp="1906zej0q1fev">
                      {received(e.at)}
                      {e.note ? ` · ${e.note}` : ''}
                    </span>
                    {e.orderId && <Link to={`/be/bestellingen/${e.orderId}`} className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-5390" data-mp="z1r4s62hjo0m">
                        <ShoppingBagIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-5391" data-mp="q3p7o1lvhfim" />
                        Bestelling bekijken
                      </Link>}
                  </span>
                  <span className={`shrink-0 text-[14px] tabular-nums ${e.amount < 0 ? 'text-muted' : 'text-ink'}`} data-id="element-5392" data-mp="2kh6mn087p421">{signedAmount(e.unit, e.amount)}</span>
                </li>)}
            </ol>
          </Panel>
        </div>

        <Panel title="Gegevens" data-id="element-5393" data-mp="3sg41bm61sppe">
          <dl className="space-y-4 text-[14px]" data-id="element-5394" data-mp="3set0nv7wfy2i">
            <div data-id="element-5395" data-mp="3si6xknxsnf2k">
              <dt className="text-[13px] text-muted" data-id="element-5396" data-mp="3odzd52ydcla5">Code</dt>
              <dd className="mt-1 flex items-center gap-2" data-id="element-5397" data-mp="3ogd3iyi3t9bd">
                <span className="select-all break-all font-mono tracking-wide text-ink" data-id="element-5398" data-mp="3jg7hbxulsf0o">{v.code}</span>
                <button type="button" onClick={() => void copy()} aria-label="Code kopiëren" className="rounded-md p-1.5 text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink" data-id="element-5399" data-mp="jyzjrf9nokkx">
                  <CopyIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5400" data-mp="b6fomoskoy4u" />
                </button>
              </dd>
            </div>
            <div data-id="element-5401" data-mp="13o1himyf9a0z">
              <dt className="text-[13px] text-muted" data-id="element-5402" data-mp="13ojoadipn6n5">Geldt voor</dt>
              <dd className="mt-0.5 text-ink" data-id="element-5403" data-mp="13purab4c0ad7">{validForLabel[v.validFor]}</dd>
            </div>
            <div data-id="element-5404" data-mp="yv8eq4mikbrj">
              <dt className="text-[13px] text-muted" data-id="element-5405" data-mp="168g1uwidanxh">Geldig tot en met</dt>
              <dd className="mt-0.5 text-ink" data-id="element-5406" data-mp="16akec2uc0v9d">{untilLabel(v.validUntil)}</dd>
            </div>
            {v.orderId && <div data-id="element-5407" data-mp="1g6ngv9t3ivwu">
                <dt className="text-[13px] text-muted" data-id="element-5408" data-mp="1g6dyo8ealt1p">Gekocht in</dt>
                <dd className="mt-0.5" data-id="element-5409" data-mp="1k88y17cnxwcu">
                  <Link to={`/be/bestellingen/${v.orderId}`} className="text-ink underline underline-offset-4 hover:text-clay" data-id="element-5410" data-mp="1k9a93btoiiuk">
                    Bestelling bekijken
                  </Link>
                </dd>
              </div>}
            {v.note && <div data-id="element-5411" data-mp="1yxdjqyio2ipp">
                <dt className="text-[13px] text-muted" data-id="element-5412" data-mp="1ywvfbeluoy6p">Notitie</dt>
                <dd className="mt-0.5 whitespace-pre-line text-ink" data-id="element-5413" data-mp="23w880fzekzh7">{v.note}</dd>
              </div>}
          </dl>
          {!design && <p className="mt-5 text-[12px] text-muted" data-id="element-5414" data-mp="27yutlz5hdo5y">Geen ontwerp "Cadeaubon" in Ontwerpen: de print gebruikt de standaardtekst.</p>}
        </Panel>
      </div>
    </div>;
}